import { v4 as uuidv4 } from 'uuid';

import { DictionaryUUIDsEnum } from 'CorePackage/DictionariesModule/enums/DictionaryUUIDsEnum';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { createOkvedSelectOption } from 'CorePackage/RegistryModule/common/createOkvedSelectOption';
import { IKnmSubject248 } from 'APIPackage/KnmModule/interfaces/IKnmSubject248';
import { InspectionResultFieldEnum } from 'APIPackage/CatalogsModule/enums/InspectionResultFieldEnum';
import { KnmResponsibleTypeCodesEnum } from 'APIPackage/CatalogsModule/enums/KnmResponsibleTypeCodesEnum';
import { getDistrictSelectOptions } from 'CorePackage/CatalogsModule/selectors/catalogsSelectors';
import { findRegionSelectOption } from 'KnmPackage/MainModule/components/regions';

import { convertKnmDataActToFormValues } from '../common/convertKnmDataActToFormValues';
import { IKnmErknmFormAct } from '../interfaces/IKnmErknmFormAct';
import { IKnmErknmFormResultDecision } from '../interfaces/IKnmErknmFormResultDecision';
import { IKnmErknmFormSubject } from '../interfaces/IKnmErknmFormSubject';
import { convertKnmDataResultDecisionsToFormValues } from '../common/convertKnmDataResultDecisionsToFormValues';
import { generateOrganizationTemplateText } from '../common/generateOrganizationTemplateText';

export const getOrganizations = async (
  knmId: number | undefined,
  created: string | null | undefined,
  initialOrganizations: IKnmSubject248[] | null | undefined,
  state: IRootState,
) => {
  let formOrganizations: IKnmErknmFormSubject[] | undefined;
  let formActs: IKnmErknmFormAct[] | undefined;
  let formResultDecisions: IKnmErknmFormResultDecision[] | undefined;

  const promises = initialOrganizations?.map(async initialOrganization => {
    const inspectionResults = initialOrganization.inspectionResults || [];
    const IRSubjectType = inspectionResults.find(
      i => i.inspectedField === InspectionResultFieldEnum.ORGANIZATION_TARGET,
    );

    const formOrganization: IKnmErknmFormSubject = {
      inn: initialOrganization.inn || undefined,
      ogrn: initialOrganization.ogrn || undefined,
      position: undefined,
      okveds: initialOrganization.okveds?.map(okved => ({
        okved: createOkvedSelectOption(okved),
        listId: okved.numGuid || uuidv4(),
      })),
      isPresentInItRegistry:
        typeof initialOrganization.isPresentInItRegistry === 'boolean'
          ? initialOrganization.isPresentInItRegistry
          : undefined,
      itAccreditationResponse: initialOrganization.itAccreditationResponse || undefined,
      smevItAccreditationRequestStatus: initialOrganization.smevItAccreditationRequestStatus || undefined,
      addressFns: initialOrganization.addressFns || undefined,
      organizationName: initialOrganization.organizationName || undefined,
      firstOrganizationName: initialOrganization.firstOrganizationName || undefined,
      mspCode: initialOrganization.mspCode || undefined,
      checkSubjectResult: initialOrganization.checkSubjectResult || null,
      dateCreate: initialOrganization.dateCreate || undefined,
      isAutocomplete:
        typeof initialOrganization.isAutocomplete === 'boolean' ? initialOrganization.isAutocomplete : undefined,
      isFiz: typeof initialOrganization.isFiz === 'boolean' ? initialOrganization.isFiz : undefined,
      guid: initialOrganization.guid || undefined,
      isDocumentOrganization: initialOrganization.isDocumentOrganization || false,
      listId: initialOrganization.guid || uuidv4(),
      isNew: initialOrganization.isNew || false,
      isEmpty: !initialOrganization.subjectType && !initialOrganization.inn && !initialOrganization.organizationName,
      nkoForeignAgentsRegistryNumber: initialOrganization.nkoForeignAgentsRegistryNumber || undefined,
      nkoForeignAgentsRegistryInclusionDate: initialOrganization.nkoForeignAgentsRegistryInclusionDate || undefined,
      nkoForeignAgentsRegistryExclusionDate: initialOrganization.nkoForeignAgentsRegistryExclusionDate || undefined,
      isNko: !!initialOrganization.nkoForeignAgentsRegistryInclusionDate,
      subjectType: initialOrganization.subjectType
        ? {
            id: 0,
            code: initialOrganization.subjectType.code,
            title: initialOrganization.subjectType.title,
            label: initialOrganization.subjectType.title,
            inspectionResult: IRSubjectType,
            value: {
              dictId: DictionaryUUIDsEnum.TARGET_ENTITY,
              dictVersionId: '',
            },
          }
        : undefined,
      kpp: initialOrganization.kpp || undefined,
      smevRequestStatus: initialOrganization.smevRequestStatus || undefined,
      rafpNza: initialOrganization.rafpNza || undefined,
      isForeignSubjectAgencyNotRegistredInRf: initialOrganization.isForeignSubjectAgencyNotRegistredInRf || undefined,
      country: initialOrganization.country
        ? {
            id: initialOrganization.country.id,
            title: initialOrganization.country.title,
            label: initialOrganization.country.title,
            value: initialOrganization.country.id,
          }
        : undefined,
      isSonko: typeof initialOrganization.isSonko === 'boolean' ? initialOrganization.isSonko : undefined,
      locationAddress: initialOrganization.locationAddress ?? undefined,
      workAddress: initialOrganization.workAddress ?? undefined,
      factualAddress: initialOrganization.factualAddress ?? undefined,
      snils: initialOrganization.snils || undefined,
      document: initialOrganization.document || undefined,
      epguNotifications: initialOrganization.epguNotifications || undefined,
      ervkId: initialOrganization.ervkId || undefined,
      inspectionResults: initialOrganization.inspectionResults || undefined,
      isFilledFromLink: initialOrganization.isFilledFromLink || undefined,
      locationnotInFIAS: initialOrganization.locationNotInFIAS ?? undefined,
      locationmanualAddress: initialOrganization.locationManualAddress ?? undefined,
      locationregion: await (async () => {
        if (!initialOrganization.locationNotInFIAS || !initialOrganization.locationAddress) {
          return undefined;
        }
        const districtList = getDistrictSelectOptions(state);

        return findRegionSelectOption(
          initialOrganization.locationAddress,
          initialOrganization.locationRegionCodeFromFns || initialOrganization.locationRegion || undefined,
          districtList,
        );
      })(),
      locationfiasRegion: await (async () => {
        if (initialOrganization.locationNotInFIAS || !initialOrganization.locationAddress) {
          return undefined;
        }
        const districtList = getDistrictSelectOptions(state);

        return findRegionSelectOption(
          initialOrganization.locationAddress,
          initialOrganization.locationRegionCodeFromFns || initialOrganization.locationFiasRegion || undefined,
          districtList,
        );
      })(),
    };

    if (!formOrganization.isDocumentOrganization) {
      if (!formOrganizations) {
        formOrganizations = [];
      }
      formOrganizations.push(formOrganization);
    }
    if (initialOrganization.act) {
      if (!formActs) {
        formActs = [];
      }
      formActs.push(
        convertKnmDataActToFormValues({
          created,
          act: initialOrganization.act,
          actOrganization: formOrganization,
          state,
        }),
      );
    }
    if (initialOrganization.resultDecisions) {
      if (!formResultDecisions) {
        formResultDecisions = [];
      }

      const newFormResultDecisions = await convertKnmDataResultDecisionsToFormValues({
        knmId,
        resultDecisions: initialOrganization.resultDecisions,
        resultDecisionsOrganization: formOrganization,
        state,
        formActs,
      });

      formResultDecisions = newFormResultDecisions
        ? [...formResultDecisions, ...newFormResultDecisions]
        : formResultDecisions;
    }
  });

  if (promises) {
    await Promise.all(promises);
  }

  if (formResultDecisions) {
    // Заполняем организацию в отвественных
    initialOrganizations?.forEach(initialOrganization => {
      if (initialOrganization.resultDecisions) {
        initialOrganization.resultDecisions.forEach(item => {
          item.responsibleEntities?.forEach(responsibleEntitie => {
            if (formResultDecisions) {
              const decisiionIndex = formResultDecisions.findIndex(i => i.guid === item.guid);

              if (decisiionIndex !== undefined && decisiionIndex > -1) {
                const entIndex = formResultDecisions[decisiionIndex].responsibleEntities?.findIndex(
                  i => i.guid === responsibleEntitie.guid,
                );

                if (entIndex !== undefined && entIndex > -1) {
                  const organization = formOrganizations?.find(i => {
                    if (responsibleEntitie.subjectType?.code === KnmResponsibleTypeCodesEnum.IUR) {
                      return (
                        i.organizationName === responsibleEntitie.organizationName &&
                        i.country?.id === responsibleEntitie.country?.id
                      );
                    }

                    if (responsibleEntitie.subjectType?.code === KnmResponsibleTypeCodesEnum.IFIZ) {
                      return (
                        i.organizationName === responsibleEntitie.organizationName &&
                        i.country?.id === responsibleEntitie.country?.id &&
                        document
                      );
                    }

                    return (
                      (i.inn && i.inn === responsibleEntitie.inn) ||
                      (i.ogrn && i.inn === responsibleEntitie.ogrn) ||
                      (i.snils && i.snils === responsibleEntitie.snils) ||
                      (i.rafpNza && i.rafpNza === responsibleEntitie.rafpNza)
                    );
                  });

                  if (organization) {
                    const label = generateOrganizationTemplateText(
                      organization?.inn || undefined,
                      organization?.ogrn || undefined,
                      organization?.organizationName || undefined,
                      organization?.rafpNza || undefined,
                    );

                    // @ts-ignore
                    if (formResultDecisions[decisiionIndex].responsibleEntities?.length > 0) {
                      // @ts-ignore
                      formResultDecisions[decisiionIndex].responsibleEntities[entIndex].organization = {
                        id: item.guid || '',
                        value: organization,
                        label,
                        isOther: false,
                      };
                    }
                  } else {
                    // @ts-ignore
                    // eslint-disable-next-line no-lonely-if
                    if (formResultDecisions[decisiionIndex].responsibleEntities?.length > 0) {
                      const label = generateOrganizationTemplateText(
                        responsibleEntitie?.inn || undefined,
                        responsibleEntitie?.ogrn || undefined,
                        responsibleEntitie?.organizationName || undefined,
                        responsibleEntitie?.rafpNza || undefined,
                      );

                      // @ts-ignore
                      formResultDecisions[decisiionIndex].responsibleEntities[entIndex].organization = {
                        id: item.guid || '',
                        value: {
                          inn: responsibleEntitie.inn || undefined,
                          ogrn: responsibleEntitie.ogrn || undefined,
                          position: responsibleEntitie.position || undefined,
                          okveds: responsibleEntitie.okveds?.map(okved => ({
                            okved: createOkvedSelectOption(okved),
                            listId: okved.numGuid || uuidv4(),
                          })),
                          addressFns: responsibleEntitie.addressFns || undefined,
                          organizationName: responsibleEntitie.organizationName || undefined,
                          firstOrganizationName: responsibleEntitie.firstOrganizationName || undefined,
                          mspCode: responsibleEntitie.mspCode || undefined,
                          checkSubjectResult: responsibleEntitie.checkSubjectResult || null,
                          dateCreate: responsibleEntitie.dateCreate || undefined,
                          isFiz: typeof responsibleEntitie.isFiz === 'boolean' ? responsibleEntitie.isFiz : undefined,
                          guid: responsibleEntitie.guid || undefined,
                          isDocumentOrganization: responsibleEntitie.isDocumentOrganization || false,
                          listId: responsibleEntitie.guid || uuidv4(),
                          isPresentInItRegistry: false,
                          isEmpty:
                            !responsibleEntitie.subjectType &&
                            !responsibleEntitie.inn &&
                            !responsibleEntitie.organizationName,
                          nkoForeignAgentsRegistryNumber:
                            responsibleEntitie.nkoForeignAgentsRegistryNumber || undefined,
                          nkoForeignAgentsRegistryInclusionDate:
                            responsibleEntitie.nkoForeignAgentsRegistryInclusionDate || undefined,
                          isNko: !!responsibleEntitie.nkoForeignAgentsRegistryInclusionDate,
                          nkoForeignAgentsRegistryExclusionDate:
                            responsibleEntitie.nkoForeignAgentsRegistryExclusionDate || undefined,
                          subjectType: responsibleEntitie.subjectType
                            ? {
                                id: responsibleEntitie.subjectType.id,
                                code: responsibleEntitie.subjectType.code,
                                title: responsibleEntitie.subjectType.name || '',
                                label: responsibleEntitie.subjectType.name || '',
                                value: {
                                  dictId: DictionaryUUIDsEnum.TARGET_ENTITY,
                                  dictVersionId: '',
                                },
                              }
                            : undefined,
                          kpp: responsibleEntitie.kpp || undefined,
                          smevRequestStatus: responsibleEntitie.smevRequestStatus || undefined,
                          rafpNza: responsibleEntitie.rafpNza || undefined,
                          isAutocomplete:
                            typeof responsibleEntitie.isAutocomplete === 'boolean'
                              ? responsibleEntitie.isAutocomplete
                              : undefined,
                          isForeignSubjectAgencyNotRegistredInRf:
                            responsibleEntitie.isForeignSubjectAgencyNotRegistredInRf || undefined,
                          country: responsibleEntitie.country
                            ? {
                                id: responsibleEntitie.country.id,
                                title: responsibleEntitie.country.title,
                                label: responsibleEntitie.country.title,
                                value: responsibleEntitie.country.id,
                              }
                            : undefined,
                          isSonko:
                            typeof responsibleEntitie.isSonko === 'boolean' ? responsibleEntitie.isSonko : undefined,
                          locationAddress: responsibleEntitie.locationAddress || undefined,
                          workAddress: responsibleEntitie.workAddress || undefined,
                          factualAddress: responsibleEntitie.factualAddress || undefined,
                          snils: responsibleEntitie.snils || undefined,
                          document: responsibleEntitie.document || undefined,
                          ervkId: responsibleEntitie.ervkId || undefined,
                          epguNotifications: responsibleEntitie.epguNotifications || undefined,
                          itAccreditationResponse: responsibleEntitie.itAccreditationResponse || undefined,
                          smevItAccreditationRequestStatus:
                            responsibleEntitie.smevItAccreditationRequestStatus || undefined,
                          inspectionResults: responsibleEntitie.inspectionResults || undefined,
                          isFilledFromLink: undefined,
                          locationregion: undefined,
                          locationnotInFIAS: undefined,
                          locationmanualAddress: undefined,
                          locationfiasRegion: undefined,
                        },
                        label,
                        isOther: true,
                      };
                    }
                  }
                }
              }
            }
          });
        });
      }
    });
  }

  return {
    formOrganizations,
    formActs,
    formResultDecisions,
  };
};
